import type { TableCellProps } from '@mui/material/TableCell';
import TableCell from '@mui/material/TableCell';
import { intent, sizes, state, surface } from '../../theme/tokens';

/**
 * TableStickyCell — a cell that stays put while the table scrolls sideways. LOCAL to the Table
 * family.
 *
 * This is the one thing people assume needs a paid grid. MUI X's `DataGrid` charges for it as "column
 * pinning"; on a real `<table>` it is `position: sticky` plus three details that are easy to miss,
 * which is the whole reason this exists as a component rather than as advice:
 *
 * 1. **It has to be opaque.** A transparent sticky cell lets the scrolling content slide visibly
 *    underneath it.
 * 2. **Being opaque means it stops answering the row.** Hover and selection are painted on the
 *    `<tr>`, and an opaque cell covers them — so a frozen column would be the one part of the row
 *    that never highlights. It repaints both itself.
 * 3. **The tint layers, it does not replace.** `state.hover` is translucent, so it goes on as a
 *    background IMAGE over the opaque colour. A selected row that is also hovered then darkens
 *    like every other row instead of sitting flat.
 *
 * For a table that manages its own columns, `DataTable`'s `stickyColumns` does all of this from a
 * column model, including the offsets. Reach for this when you are writing the rows yourself.
 *
 * ```tsx
 * <TableRow hover>
 *   <TableStickyCell sticky="left" edge>{row.name}</TableStickyCell>
 *   <TableCell>…</TableCell>
 *   <TableStickyCell sticky="right" edge align="right">{actions}</TableStickyCell>
 * </TableRow>
 * ```
 */
export interface TableStickyCellProps extends TableCellProps {
  /** Which edge the cell clings to. */
  sticky: 'left' | 'right';
  /**
   * Distance from that edge, in px. `0` for the first frozen cell; for a second one, pass the
   * total width of everything frozen before it. Default `0`.
   */
  offset?: number;
  /**
   * Draw the boundary between the frozen block and the scrolling content. Set it on the LAST
   * frozen-left cell and the FIRST frozen-right one — the inner cells of a frozen block share
   * their neighbours' edges and would otherwise draw a line inside the block.
   */
  edge?: boolean;
  /**
   * `true` for a cell inside `TableHead`. It only changes the stacking order: a header cell that is
   * sticky in BOTH directions has to sit above the body cells that are sticky in one, or a row
   * scrolled under the header paints over it.
   */
  header?: boolean;
}

/*
 * The stacking order. Named rather than inline because the bug it prevents is invisible until two
 * of these meet: equal z-index makes the winner the later element in the document, so a body cell
 * would paint over a sticky header and only while scrolled.
 */
const LAYER = { body: 1, header: 3 } as const;

export function TableStickyCell({
  sticky,
  offset = 0,
  edge = false,
  header = false,
  sx,
  ...cellProps
}: TableStickyCellProps) {
  const border = `${sizes.border.bd1}px solid ${intent.neutral.border}`;
  return (
    <TableCell
      {...cellProps}
      sx={[
        {
          position: 'sticky',
          [sticky]: `${offset}px`,
          zIndex: header ? LAYER.header : LAYER.body,
          /*
           * Opaque, via a variable so a table dropped on a tinted surface can be corrected in one
           * place instead of per cell: set `--Table-stickySurface` on the container.
           */
          backgroundColor: `var(--Table-stickySurface, ${surface.white})`,
          ...(edge ? { [sticky === 'left' ? 'borderRight' : 'borderLeft']: border } : null),

          // The row's own feedback, repainted — see the note above on why this is necessary.
          '.MuiTableRow-root.Mui-selected > &': { backgroundColor: intent.primary.subtle },
          '.MuiTableRow-hover:hover > &': {
            backgroundImage: `linear-gradient(${state.hover}, ${state.hover})`,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
